// Configuración predeterminada del menú homologado
// Este formato se aplica a empresas nuevas y sirve como base para personalizaciones

export interface DefaultMenuConfig {
  version: string;
  format: 'homologated' | 'custom';
  sections: {
    dashboard: {
      enabled: boolean;
      position: 'top';
      modules: string[];
    };
    otros: {
      enabled: boolean;
      title: string;
      allowCustomGroups: boolean;
      modules: string[]; // Se llenará dinámicamente con módulos disponibles
    };
    herramientas: {
      enabled: boolean;
      title: string;
      fixedModules: Array<{
        code: string;
        name: string;
        route: string;
        icon: string;
      }>;
    };
  };
  customGroups: any[]; // Compatible con Json - array vacío por defecto
  moduleOrder: Record<string, string[]>; // Orden por defecto de módulos por categoría
  [key: string]: any; // Index signature para compatibilidad con Json
}

// Configuración predeterminada homologada
export const DEFAULT_MENU_CONFIG: DefaultMenuConfig = {
  version: '1.0.0',
  format: 'homologated',
  sections: {
    dashboard: {
      enabled: true,
      position: 'top',
      modules: ['dashboard']
    },
    otros: {
      enabled: true,
      title: 'Otros',
      allowCustomGroups: false, // En formato homologado no se permiten grupos custom por defecto
      modules: [] // Se llena dinámicamente
    },
    herramientas: {
      enabled: true,
      title: 'Herramientas y Soporte',
      fixedModules: [
        {
          code: 'implementation',
          name: 'Implementación',
          route: '/implementation',
          icon: 'Code'
        },
        {
          code: 'user-manual',
          name: 'Manual de Usuario', 
          route: '/user-manual',
          icon: 'FileText'
        }
      ]
    }
  },
  customGroups: [],
  moduleOrder: {
    'main': [], // Orden de módulos principales - se define según los módulos disponibles
    'otros': [] // Orden específico para la sección "otros"
  }
};

// Función para generar configuración predeterminada para una empresa
export const generateDefaultConfigForCompany = (availableModules: Array<{
  module_id: string;
  module_code: string; 
  module_name: string;
  module_route: string;
  parent_id: string | null;
  sort_order: number;
}>): DefaultMenuConfig => {
  // Filtrar módulos principales (sin padre) y excluir dashboard
  const mainModules = availableModules
    .filter(m => !m.parent_id && m.module_route !== '/' && m.module_route !== '/dashboard')
    .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999))
    .map(m => m.module_id);

  return {
    ...DEFAULT_MENU_CONFIG,
    sections: {
      ...DEFAULT_MENU_CONFIG.sections,
      otros: {
        ...DEFAULT_MENU_CONFIG.sections.otros,
        modules: mainModules
      }
    },
    moduleOrder: {
      main: mainModules,
      otros: mainModules
    }
  };
};

// Función para aplicar configuración predeterminada a corporate group
export const applyDefaultMenuConfig = async (
  corporateGroupId: string, 
  availableModules?: Array<{
    module_id: string;
    module_code: string;
    module_name: string; 
    module_route: string;
    parent_id: string | null;
    sort_order: number;
  }>
) => {
  const config = availableModules 
    ? generateDefaultConfigForCompany(availableModules)
    : DEFAULT_MENU_CONFIG;

  // Esta función se puede llamar desde el backend o desde un hook específico
  return config;
};

// Función para verificar si una configuración necesita actualización
export const needsConfigUpdate = (currentConfig: any): boolean => {
  if (!currentConfig) return true;
  
  // Verificar si tiene la estructura homologada
  if (currentConfig.format !== 'homologated') return true;
  if (currentConfig.version !== DEFAULT_MENU_CONFIG.version) return true;
  
  return false;
};